import type { RouteLocationRaw } from 'vue-router'
import {
  getComposeLocalService,
  getNotificationService,
  getScheduledNotSentService,
  getScheduledParametersService,
  getScheduledService,
  getSentSmsLocalStorageService,
  getTimezoneService,
  getTranslateService,
} from '@/core/container/ioc'
import { useConfirmation } from '@/composition/confirmation'
import ScheduledMessage from '@/data/models/domain/scheduled/ScheduledMessage'
import { redirect } from '@/composition/router'
import { ScheduledType } from '@/components/views/scheduled/types'
import { useTypedWrapper } from '@/composition/modal'
import { formatDateForTimezone } from '@/composition/date'

import type ScheduledNotSentService from '@/services/domain/scheduled/scheduledNotSentService'
import type ScheduledService from '@/services/domain/scheduled/scheduledService'
import { DATE_FORMATES, type DateFormatesType } from '@/services/types'
import type Timezone from '@/data/models/domain/Timezone'
import { ScheduledRoutes } from '@/routes/user/scheduled/types'
import { useLayoutMenuFaceter } from '@/composition/layoutMenu'
import { useComposeMessageNavigation } from '@/composition/compose/useComposeMessageNavigation'
import type { ScheduledFacet, ScheduledFacetType } from '@/services/domain/scheduled/types'
import TmAbsurdError from '@/core/error/tmAbsurdError'

export interface ScheduledRetryModalParams {
  messageIds: string[]
  isSelectedAll?: boolean
}
export const ScheduledRetryModal = useTypedWrapper<ScheduledRetryModalParams>('scheduledRetryModal')

export const useScheduledMessageDeleteAction = (
  messageId: string,
  scheduledType: ScheduledType,
  onSuccess = () => {},
) => {
  const scheduledMessageServices: Record<ScheduledType, ScheduledNotSentService | ScheduledService> = {
    [ScheduledType.NOT_SENT]: getScheduledNotSentService(),
    [ScheduledType.COMPLETED]: getScheduledService(),
    [ScheduledType.PAUSED]: getScheduledService(),
    [ScheduledType.UPCOMING]: getScheduledService(),
  }

  return useScheduledMessageConfirmation({
    service: scheduledMessageServices[scheduledType],
    messageId,
    scheduledType,
    onSuccess,
  })
}

type ScheduledMessageDeleteConfirmationParams = {
  service: ScheduledNotSentService | ScheduledService
  messageId: string
  scheduledType: ScheduledType
  onSuccess: () => void
}

export const useScheduledMessageConfirmation = ({
  service,
  messageId,
  scheduledType,
  onSuccess,
}: ScheduledMessageDeleteConfirmationParams) => {
  const translateService = getTranslateService()
  const translateKey = scheduledType === ScheduledType.UPCOMING ? 'upcoming' : 'default'

  const callback = async () => {
    await service.delete(messageId)
    getNotificationService().successT(`DeleteScheduledSingleConfirmation.success.${translateKey}`)
    onSuccess()
  }
  const title = translateService.t(`DeleteScheduledSingleConfirmation.title.${translateKey}`)
  const deleteMessagesText = translateService.t(`DeleteScheduledSingleConfirmation.deleteMessages.${translateKey}`)
  const config = {
    title,
    text: {
      translateKey: 'DeleteScheduledSingleConfirmation.text',
      slots: [{ name: 'deleteMessages', value: deleteMessagesText }],
    },
  }
  return useConfirmation().confirmDelete('', callback, config)
}
export const useScheduledMessagePauseAction = (messageIds: string[], isAllSelected = false, onSuccess?: () => void) => {
  const translateService = getTranslateService()
  const notificationService = getNotificationService()
  const scheduledService = getScheduledService()

  return useConfirmation().confirm(
    async () => {
      if (isAllSelected) {
        await scheduledService.pauseAllMessages()
      } else {
        await scheduledService.pauseMessages(messageIds)
      }
      notificationService.notifySuccess(
        translateService.tc('scheduledMessagesPauseConfirmation.successText', messageIds.length),
      )
      onSuccess?.()
    },
    {
      submitText: translateService.t('scheduledMessagesPauseConfirmation.submitText'),
      submitLoadingText: translateService.t('scheduledMessagesPauseConfirmation.submitLoadingText'),
      title: translateService.tc('scheduledMessagesPauseConfirmation.title', messageIds.length),
      text: translateService.tc('scheduledMessagesPauseConfirmation.text', messageIds.length),
    },
  )
}

export const useScheduledMessageResumeAction = (messageIds: string[], isAllSelected = false) =>
  useScheduledMessageResumeConfirmation({
    service: getScheduledService(),
    messageIds,
    isAllSelected,
  })

type ScheduledMessageResumeConfirmationParams = {
  service: ScheduledService
  messageIds: string[]
  isAllSelected: boolean
}

export const useScheduledMessageResumeConfirmation = ({
  service,
  messageIds,
  isAllSelected,
}: ScheduledMessageResumeConfirmationParams) => {
  const translateService = getTranslateService()
  const notificationService = getNotificationService()

  return useConfirmation().confirm(
    async () => {
      if (isAllSelected) {
        await service.resumeAllMessages()
      } else {
        await service.resumeMessages(messageIds)
      }
      notificationService.notifySuccess(
        translateService.tc('scheduledMessagesResumeConfirmation.successText', messageIds.length),
      )
    },
    {
      submitText: translateService.t('scheduledMessagesResumeConfirmation.submitText'),
      submitLoadingText: translateService.t('scheduledMessagesResumeConfirmation.submitLoadingText'),
      title: translateService.tc('scheduledMessagesResumeConfirmation.title', messageIds.length),
      text: translateService.tc('scheduledMessagesResumeConfirmation.text', messageIds.length),
    },
  )
}

export const useScheduledCompletedSmsEditAndResend = () => {
  const scheduledService = getScheduledService()
  const scheduledParametersService = getScheduledParametersService()
  const sentSmsLocalStorageService = getSentSmsLocalStorageService()
  const composeLocalService = getComposeLocalService()

  const { composeMessage } = useComposeMessageNavigation()

  const editAndResend = async (messageId: string) => {
    if (!scheduledService.findEntityByIdOrNull(messageId)) {
      await scheduledService.fill(messageId)
    }

    const message = scheduledService.findEntityByIdOrFail(messageId)
    const parameters = scheduledParametersService.findEntityByIdOrFail(message.parametersId, [
      'contacts',
      'lists',
      'filteredViews',
    ])

    // The original schedule is already in the past, so the draft starts without a send time
    await composeLocalService.save({
      message: parameters.text,
      sender: message.senderSourcePhone,
      recipients: sentSmsLocalStorageService.prepareBulkComposeLocalStorageEntry({
        contacts: parameters.contacts,
        groups: parameters.lists,
        filteredViews: parameters.filteredViews,
        numbers: parameters.numbers,
      }),
      version: 1,
    })

    await composeMessage()
  }

  return {
    editAndResend,
  }
}

export const redirectByScheduledMessageStatus = (scheduledMessage: ScheduledMessage): void => {
  const routesMap = getScheduledRoutesMap()

  const route = routesMap[scheduledMessage.status]

  redirect(route as RouteLocationRaw)
}

export const getScheduledRoutesMap = () => ({
  [ScheduledType.COMPLETED]: { name: ScheduledRoutes.completed },
  [ScheduledType.PAUSED]: { name: ScheduledRoutes.paused },
  [ScheduledType.NOT_SENT]: { name: ScheduledRoutes.notSent },
  [ScheduledType.UPCOMING]: { name: ScheduledRoutes.upcoming },
})

export const getScheduledLabelMap = () => {
  const ts = getTranslateService()

  return {
    [ScheduledType.COMPLETED]: ts.t('entities.scheduledMessage.status.completed'),
    [ScheduledType.PAUSED]: ts.t('entities.scheduledMessage.status.paused'),
    [ScheduledType.NOT_SENT]: ts.t('entities.scheduledMessage.status.notSent'),
    [ScheduledType.UPCOMING]: ts.t('entities.scheduledMessage.status.upcoming'),
  }
}

export const getScheduledTitleLabelMap = () => {
  const ts = getTranslateService()

  return {
    [ScheduledType.COMPLETED]: ts.t('entities.scheduledMessage.status.completed'),
    [ScheduledType.PAUSED]: ts.t('entities.scheduledMessage.status.paused'),
    [ScheduledType.NOT_SENT]: ts.t('entities.scheduledMessage.status.notSent'),
    [ScheduledType.UPCOMING]: ts.t('entities.scheduledMessage.status.scheduled'),
  }
}

export const getNextSendScheduledMessage = (scheduledMessage: ScheduledMessage) => {
  const timezoneService = getTimezoneService()

  const nextSendTimezone = timezoneService.getEntityByTimezone(scheduledMessage.timezone) || undefined

  return formatSendTimeScheduledMessage(scheduledMessage.nextSend, nextSendTimezone)
}

export const formatSendTimeScheduledMessage = (
  nextSend: Date | string,
  timezone?: Timezone,
  format: DateFormatesType = DATE_FORMATES.DATE_TIME_WITH_YEAR_AND_TIMEZONE,
) => formatDateForTimezone(nextSend, timezone, format, false)

type NumberGroupByType = {
  sms: number | null
  email: number | null
}

export const useLayoutMenuScheduledFaceter = () => {
  const { getNumber } = useLayoutMenuFaceter<ScheduledFacet>(ScheduledMessage)

  const getNumberGroupByType = (type: ScheduledFacetType): NumberGroupByType => {
    if (type === 'upcoming') {
      return {
        sms: getNumber('upcoming'),
        email: getNumber('emailUpcoming'),
      }
    }

    if (type === 'paused') {
      return {
        sms: getNumber('paused'),
        email: getNumber('emailPaused'),
      }
    }

    if (type === 'notSent') {
      return {
        sms: getNumber('notSent'),
        email: getNumber('emailNotSent'),
      }
    }

    if (type === 'completed') {
      return {
        sms: getNumber('completed'),
        email: getNumber('emailCompleted'),
      }
    }

    throw new TmAbsurdError(type)
  }

  return {
    getNumberGroupByType,
  }
}
